import { useRequiredContext } from "@redotech/react-util/context";
import { useRpcData } from "@redotech/react-util/rpc-data";
import { readReturnsCenterId } from "@redotech/redo-return-core-common/returns-center-id";
import type { GradeMapping } from "@redotech/redo-return-settings-common/grade-mapping";
import type { GradeSortDispositionOverride } from "@redotech/redo-return-settings-common/returns-center-grade-overrides";
import { useMemo } from "react";
import { RedoManagedReturnsRpcClientContext } from "./rpc-client";

export interface ReturnsCenterGradeOverrides {
  /** Keyed by the decoded returns center id, so `rctr_` and raw ids match. */
  get(returnsCenterId: string): readonly GradeSortDispositionOverride[];
}

/** Saved grade mappings from CRDB; `undefined` until loaded so editors never start from the stale team copy. */
export function useGradeMappings(): {
  gradeMappings: readonly GradeMapping[] | undefined;
  centerOverrides: ReturnsCenterGradeOverrides | undefined;
  refresh: () => void;
} {
  const rpcClient = useRequiredContext(RedoManagedReturnsRpcClientContext);
  const { value, refresh } = useRpcData(rpcClient, "getGradeMappings", {
    input: {},
  });
  const centerOverrides = useMemo(() => {
    if (!value) {
      return undefined;
    }
    const byCenter = new Map(
      value.returnsCenterOverrides.map((center) => [
        center.returnsCenterId,
        center.gradeSortDispositions,
      ]),
    );
    return {
      get: (returnsCenterId: string) =>
        byCenter.get(readReturnsCenterId(returnsCenterId)?.toString() ?? "") ??
        [],
    };
  }, [value]);
  return { gradeMappings: value?.gradeMappings, centerOverrides, refresh };
}
